import type { ReactNode } from "react";
import { cn } from "@dearbill/shared";

/**
 * Where a step sits in the flow: `done` (it has what it needs), `current` (the
 * first step not done, where the pharmacist's eye should go) or `upcoming` (a
 * later step). `CostToolTab` computes it once, so the three cards agree on
 * which step is next.
 */
export type StepStatus = "done" | "current" | "upcoming";

/** The step marker's announced state, which the tests also assert against. */
function stepLabel(step: number, status: StepStatus): string {
  if (status === "done") return `Step ${step} done`;
  if (status === "current") return `Step ${step} — current step`;
  return `Step ${step} — not started yet`;
}

/**
 * The cost tool's step header: the step number (a check once the step is done),
 * the step title, and a "Done" word once the step is satisfied. It only shows
 * and announces progress; the flow never blocks on step order, so a pharmacist
 * may add drugs before the plan.
 */
export function SectionHeading({
  id,
  step,
  status,
  children,
}: {
  id: string;
  /** 1-based step number shown as the marker (until the check replaces it). */
  step: number;
  status: StepStatus;
  children: ReactNode;
}): ReactNode {
  return (
    <div className="flex items-start gap-5">
      {/* role=img with an aria-label: the marker is the state ("Step 1 done"),
          so screen readers announce it and tests find it without a test id. */}
      <span
        role="img"
        aria-label={stepLabel(step, status)}
        className={cn(
          "flex w-5 shrink-0 items-center justify-center text-2xl font-semibold leading-[30px]",
          status === "done" && "text-success",
          status === "current" && "text-ember",
          status === "upcoming" && "text-slate-teal/60",
        )}
      >
        {status === "done" ? <CheckIcon size={22} /> : step}
      </span>
      <h3
        id={id}
        className={cn(
          "min-w-0 flex-1 pt-0.5 text-[19px] font-bold leading-[26px]",
          status === "upcoming" ? "text-slate-teal" : "text-foreground",
        )}
      >
        {children}
      </h3>
      {status === "done" && (
        <span className="text-success shrink-0 pt-1 text-sm font-medium">Done</span>
      )}
    </div>
  );
}

/**
 * Check for the done marker, without the circle the shared `CheckCircleIcon`
 * draws. It is aria-hidden; the marker's aria-label carries the meaning.
 */
function CheckIcon({ size }: { size: number }): ReactNode {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2.25}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="m5 13 4 4L19 7" />
    </svg>
  );
}
